import styled from '@emotion/styled'
import { ExtranetInviteTokenDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTable,
  ExtranetTableColumn,
  ExtranetTableRow,
  MediaQuery,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacH3, PharmacTab, PharmacTabs } from '@pharmac/ui'
import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import React from 'react'
import { ExtranetViewReturnButton } from '../../components/extranet/ExtranetViewReturnButton'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { InviteLinkItem } from '../../components/invite-links/InviteLinkItem'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'

const Title = styled(PharmacH3)`
  ${MediaQuery.maxTablet} {
    padding-left: var(--spacing-medium);
  }
`

export const __CUSTOMER_INVITE_LINKS_QUERY_KEY = '__getCustomerInviteLinks'

export const CustomerInviteLinksView = () => {
  const { minDesktop } = useBreakpoint()
  const { t } = useLanguage(['extranet', 'common'])
  const { customer: customership } = useCustomerContext()

  const getInviteTokens = (id?: number): UseQueryOptions<Array<ExtranetInviteTokenDto>> => ({
    queryFn: async () => await api.customers.getCustomerInviteTokens(id as number),
    queryKey: [__CUSTOMER_INVITE_LINKS_QUERY_KEY, id],
    enabled: typeof id === 'number',
    refetchInterval: 10000,
  })

  const { data: allInviteTokens } = useQuery(getInviteTokens(customership?.id))

  if (!customership) return

  return (
    <ExtranetWrapper>
      {minDesktop && (
        <ExtranetContainer
          full
          paddingMediumVertical
        >
          <ExtranetViewReturnButton />
        </ExtranetContainer>
      )}
      <ExtranetContainer
        alignCenter
        marginMediumTop
        marginLargeBottom
      >
        {!minDesktop && <ExtranetViewReturnButton />}
        <Title color="purple">{t('customer.title')}</Title>
      </ExtranetContainer>
      <PharmacTabs singleTab>
        {[
          <PharmacTab
            handle={t('inviteLinks.openInvites')}
            key="invite-links-tab"
          >
            <ExtranetContainer
              full
              paddingLargeVertical
              paddingSmallHorizontal={!minDesktop}
            >
              <ExtranetTable
                style={{ fontFamily: 'var(--font-family-secondary)', fontWeight: 300 }}
              >
                {minDesktop && (
                  <ExtranetTableRow
                    style={{
                      backgroundColor: 'var(--color-accent-4)',
                      border: 'none',
                      fontWeight: 600,
                    }}
                  >
                    <ExtranetTableColumn
                      size={1}
                      marginSmallLeft
                    >
                      {t('inviteLinks.inviteCreator')}
                    </ExtranetTableColumn>
                    <ExtranetTableColumn size={1.5}>
                      {t('inviteLinks.InvitationLinkRecipientEmail')}
                    </ExtranetTableColumn>
                    <ExtranetTableColumn size={1}>{t('inviteLinks.expires')}</ExtranetTableColumn>
                    <ExtranetTableColumn
                      size={1}
                      style={{ justifyContent: 'center' }}
                    >
                      {t('inviteLinks.usable')}
                    </ExtranetTableColumn>
                    <ExtranetTableColumn size={1}>{t('inviteLinks.roles')}</ExtranetTableColumn>
                    <ExtranetTableColumn size={1}>
                      {t('inviteLinks.cancelInviteLink')}
                    </ExtranetTableColumn>
                    <ExtranetExpandArrowColumn />
                  </ExtranetTableRow>
                )}
                <React.Fragment>
                  {allInviteTokens?.map((token) => (
                    <InviteLinkItem
                      key={token.id}
                      token={token}
                    />
                  ))}
                </React.Fragment>
              </ExtranetTable>
            </ExtranetContainer>
          </PharmacTab>,
        ]}
      </PharmacTabs>
    </ExtranetWrapper>
  )
}
